import { fg } from '@atlaskit/platform-feature-flags';

import { type FireAnalyticsEvent } from '../../types';

import { buildFullPageSearchMlLensEnvelope } from './build-full-page-search-ml-lens-envelope';
import { type CommonAttributes } from './types';

type FullPageSearchOperationalAnalyticsProps = {
	success: boolean;
	commonAttributes: CommonAttributes;
	/** Current user id; added to the ml_lens metric-set tags as `user_id`. */
	userId?: string | null;
};

export const onFullPageSearchOperational = ({
	success,
	commonAttributes,
	userId,
}: FullPageSearchOperationalAnalyticsProps): FireAnalyticsEvent => {
	// Attach the `ml_lens` envelope on success so the ML Lens transformer can
	// derive per-search connector metrics. The single `total_connectors` metric
	// is the number of products available to the search.
	// Gated by `rovo-search-ml-lens`; `taskFail` never carries the envelope.
	// The builder returns `null` when there is no `userId`, so the tag is
	// omitted entirely in that case rather than emitted without a `user_id`.
	const mlLensEnvelope =
		success && fg('rovo-search-ml-lens')
			? buildFullPageSearchMlLensEnvelope(commonAttributes.availableProducts.length, userId)
			: null;
	const mlLens = mlLensEnvelope ? { ml_lens: mlLensEnvelope } : undefined;

	return {
		eventType: 'operational',
		action: success ? 'taskSuccess' : 'taskFail',
		actionSubject: 'ui',
		attributes: {
			commonAttributes,
			additionalAttributes: {
				task: 'unifiedSearch',
				capability: 'fullPageSearchResult',
				// This edition is not exact matching the need for the AutoHots
				// A follow up is to find the current primary product edition and log that.
				// Currently we only have confluence edition in the productEdition object, so logging the entire object for now.
				edition: commonAttributes.productEdition,
				// Confirmed with AutoHots team this is mandatory
				// and base on "Can I log this" doc, we can log this.
				// https://hello.atlassian.net/wiki/spaces/Monitoring/pages/5816497997/Vitalstats+Onboarding+contract?focusedCommentId=5864073069
				// https://hello.atlassian.net/wiki/spaces/SECURITY/pages/236070634/Can+I+Log+This
				hostname: window.location.hostname,
				...(mlLens ?? {}),
			},
		},
	};
};
